/**
 * Realtime SSE client helpers.
 *
 * The backend exposes `GET /api/events/stream` (see /app/backend/core/realtime/routes.py).
 * It authenticates via either:
 *   - cookie `access_token` (when same-origin)
 *   - query param `?token=<jwt>` (our default — we store JWT in localStorage)
 *
 * EventSource cannot set custom headers, so we always pass the token via query.
 */
import { backendOrigin, getToken } from "./api";

/**
 * Build the SSE URL for /api/events/stream.
 * @param {string[]|null} topics — optional topic filter (null = all topics).
 */
export function buildRealtimeStreamUrl(topics = null) {
  const base = `${backendOrigin()}/api/events/stream`;
  const params = new URLSearchParams();
  const token = getToken();
  if (token) params.set("token", token);
  if (topics && topics.length) params.set("topics", topics.join(","));
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/**
 * Minimal SSE wrapper with automatic exponential backoff reconnect.
 * Status callback receives one of: "connecting" | "live" | "reconnecting" | "offline" | "error".
 */
export class RealtimeClient {
  constructor({ topics = null, onStatus = null } = {}) {
    this.topics = topics;
    this.onStatus = onStatus || (() => {});
    this.handlers = new Map(); // topic -> Set<fn>
    this.es = null;
    this.retry = 0;
    this.retryTimer = null;
    this.closed = false;
  }

  on(topic, fn) {
    if (!this.handlers.has(topic)) this.handlers.set(topic, new Set());
    this.handlers.get(topic).add(fn);
    if (this.es) this._wireListener(topic);
    return () => this.off(topic, fn);
  }

  off(topic, fn) {
    const set = this.handlers.get(topic);
    if (set) set.delete(fn);
  }

  _wireListener(topic) {
    if (!this.es || this.es._wiredTopics?.has(topic)) return;
    if (!this.es._wiredTopics) this.es._wiredTopics = new Set();
    this.es._wiredTopics.add(topic);
    this.es.addEventListener(topic, (e) => {
      let parsed;
      try {
        parsed = JSON.parse(e.data);
      } catch {
        parsed = e.data;
      }
      const fns = this.handlers.get(topic);
      if (fns) fns.forEach((fn) => { try { fn(parsed, e); } catch (err) { console.warn("realtime handler error", err); } });
    });
  }

  connect() {
    if (this.closed) return;
    this._notify("connecting");
    try {
      const url = buildRealtimeStreamUrl(this.topics);
      const es = new EventSource(url);
      this.es = es;

      es.onopen = () => {
        this.retry = 0;
        this._notify("live");
      };

      es.addEventListener("ready", () => {
        this.retry = 0;
        this._notify("live");
      });

      // Wire all current listeners
      for (const topic of this.handlers.keys()) this._wireListener(topic);

      es.onerror = () => {
        if (this.closed) return;
        // EventSource will auto-reconnect on its own, but the browser sometimes
        // keeps a dead socket. We close it and reopen with backoff so we control state.
        this._notify("reconnecting");
        try { es.close(); } catch {}
        this.es = null;
        this._scheduleReconnect();
      };
    } catch (err) {
      console.warn("RealtimeClient connect failed", err);
      this._notify("error");
      this._scheduleReconnect();
    }
  }

  _scheduleReconnect() {
    if (this.closed) return;
    if (this.retryTimer) return;
    const delay = Math.min(30_000, 1000 * Math.pow(2, this.retry));
    this.retry++;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, delay);
  }

  close() {
    this.closed = true;
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null; }
    if (this.es) { try { this.es.close(); } catch {} this.es = null; }
    this._notify("offline");
  }

  _notify(status) {
    try { this.onStatus(status); } catch {}
  }
}
